/** Helpers for generic-rest-api smoke probes (wizard + secrets registry row test). */

const ALLOWED_METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Normalize a probe path fragment to a rooted path Tools API accepts (must be relative).
 * @param {string} pathRaw
 */
export function normalizeGenericRestProbePath(pathRaw) {
  const s = String(pathRaw || '').trim();
  if (!s) return '/';
  const lower = s.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return '/';
  return s.startsWith('/') ? s : `/${s}`;
}

/**
 * First plausible ``METHOD path`` route in ``API_SPECIFICATION`` (markdown / digest text).
 *
 * Skips placeholders (``REPLACE_ME``) and bare URLs — used when registry rows lack
 * explicit ``PROBE_METHOD`` / ``PROBE_PATH``.
 *
 * @param {string} specText
 * @returns {{ method: string, path: string } | null}
 */
export function parseFirstHttpRouteFromApiSpec(specText) {
  if (!specText || typeof specText !== 'string') return null;
  const re = /\b(GET|POST|PUT|PATCH|DELETE)\s+(\/[^\s`'"#]+|[A-Za-z0-9_][^\s`'"#]*)/gi;
  let m;
  while ((m = re.exec(specText)) !== null) {
    const method = String(m[1] || '').toUpperCase();
    let path = String(m[2] || '').trim();
    if (!ALLOWED_METHODS.has(method) || !path) continue;
    if (/REPLACE_ME/i.test(path)) continue;
    if (/^https?:\/\//i.test(path)) continue;
    path = normalizeGenericRestProbePath(path);
    return { method, path };
  }
  return null;
}

/**
 * @param {Record<string, unknown>} config  Registry ``row.config`` or wizard ``builtConfig``
 * @returns {{ method: string, path: string }}
 */
export function probeRequestFromGenericRestConfig(config) {
  const c = config && typeof config === 'object' ? config : {};
  const meth = String(c.PROBE_METHOD || '').trim().toUpperCase();
  const rawPath = String(c.PROBE_PATH || '').trim();
  if (ALLOWED_METHODS.has(meth) && rawPath) {
    return { method: meth, path: normalizeGenericRestProbePath(rawPath) };
  }
  const parsed = parseFirstHttpRouteFromApiSpec(String(c.API_SPECIFICATION || ''));
  if (parsed) return parsed;
  return { method: 'GET', path: '/' };
}

/** Default smoke path in the wizard before the user picks a real route (matches ``ToolGenericRestConfigWizard``). */
export const WIZARD_PROBE_PLACEHOLDER_PATH = '/REPLACE_ME_health';

/**
 * Initial probe method/path when opening the wizard from ``initialEntry.config`` (registry or agent).
 *
 * Order: persisted ``PROBE_*`` → first route in ``API_SPECIFICATION`` → placeholder path.
 *
 * @param {Record<string, unknown>} seedConfig
 * @returns {{ method: string, path: string }}
 */
export function inferWizardProbeSeed(seedConfig) {
  const c = seedConfig && typeof seedConfig === 'object' ? seedConfig : {};
  const meth = String(c.PROBE_METHOD || '').trim().toUpperCase();
  const rawPath = String(c.PROBE_PATH || '').trim();
  if (ALLOWED_METHODS.has(meth) && rawPath) {
    return { method: meth, path: normalizeGenericRestProbePath(rawPath) };
  }
  const parsed = parseFirstHttpRouteFromApiSpec(String(c.API_SPECIFICATION || ''));
  if (parsed) return parsed;
  return { method: 'GET', path: WIZARD_PROBE_PLACEHOLDER_PATH };
}

/**
 * @param {{ config?: Record<string, unknown>, probeMethod?: string, probePath?: string } | null | undefined} row
 *   List rows from ``GET …/secrets-registry`` include ``probeMethod`` / ``probePath`` (config is omitted).
 */
export function probeRequestFromGenericRestRegistryRow(row) {
  const cfgBase = row && typeof row.config === 'object' && row.config ? row.config : {};
  const cfg = { ...cfgBase };
  const r = row || {};
  const pmList = typeof r.probeMethod === 'string' ? r.probeMethod.trim().toUpperCase() : '';
  const ppList = r.probePath != null ? String(r.probePath).trim() : '';
  if (pmList && ALLOWED_METHODS.has(pmList)) cfg.PROBE_METHOD = pmList;
  if (ppList) cfg.PROBE_PATH = ppList;
  return probeRequestFromGenericRestConfig(cfg);
}
